// ================================================
// Manual Chart Generator with Auto Canvas Creation
//
// USAGE:
// Add data-plugin="chart" directly to the <table> and specify:
//
// Required:
//   - data-plugin-chart-target: CSS selector for the container (div OR canvas)
//
// Required Attribute:
//   - data-plugin-chart-type: chart type ('bar', 'line', 'pie', 'doughnut', 'radar', etc.)
//
// Optional Attributes:
//   - data-plugin-chart-title: title of the chart
//   - data-plugin-chart-colors: comma-separated list of rgba() or hex colors
//
// Example:
// <table id="myTable" 
//   data-plugin="chart"
//   data-plugin-chart-target="#myContainer"
//   data-plugin-chart-type="line"
//   data-plugin-chart-title="Sales Over Time"
//   data-plugin-chart-colors="#FF6384,#36A2EB,#FFCE56">
//   ...
// </table>
//
// <div id="myContainer"></div> <!-- Canvas will be created inside automatically -->
//
// Possible Chart Types:
// ================================================
// | Chart Name      | Chart Type   |
// |-----------------|-------------|
// | Bar Chart       | bar         |
// | Line Chart      | line        |
// | Pie Chart       | pie         |
// | Doughnut Chart  | doughnut    |
// | Radar Chart     | radar       |
// | Polar Area      | polarArea   |
// | Scatter Plot    | scatter     |
// | Bubble Chart    | bubble      |
// ================================================

import Chart from 'chart.js/auto';

const chartInstances = new Map();

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('table[data-plugin="chart"]').forEach(table => {
    const targetSelector = table.getAttribute('data-plugin-chart-target');
    const chartType = table.getAttribute('data-plugin-chart-type');

    if (!targetSelector || !chartType) {
      console.error('Missing data-plugin-chart-target or data-plugin-chart-type attribute.');
      return;
    }

    let target = document.querySelector(targetSelector);

    if (!target) {
      console.error('Target not found for selector:', targetSelector);
      return;
    }

    let canvas;
    if (target.tagName.toLowerCase() === 'canvas') {
      // If target is already a canvas, use it
      canvas = target;
    } else {
      // Otherwise create a canvas inside the target
      canvas = document.createElement('canvas');
      target.innerHTML = ''; // Clear container
      target.appendChild(canvas);
    }

    const options = {
      chartType: chartType,
      title: table.getAttribute('data-plugin-chart-title'),
      colors: parseColors(table.getAttribute('data-plugin-chart-colors')),
    };

    // Initial render
    renderChartFromTable(table, canvas, options);

    // Observe table changes and re-render
    const observer = new MutationObserver(() => {
      renderChartFromTable(table, canvas, options);
    });
    observer.observe(table, { childList: true, subtree: true });
  });
});

function renderChartFromTable(table, canvas, options) {
  const { labels, datasets } = extractTableData(table, options);

  const chartJSConfig = {
    type: options.chartType,
    data: {
      labels: labels,
      datasets: datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: options.title ? {
          display: true,
          text: options.title,
          font: { size: 18 }
        } : undefined,
        legend: {
          display: datasets.length > 1
        },
      },
      scales: (['pie', 'doughnut'].includes(options.chartType)) ? {} : {
        y: { beginAtZero: true }
      }
    }
  };

  // Destroy existing chart if present
  if (chartInstances.has(canvas)) {
    chartInstances.get(canvas).destroy();
  }

  const ctx = canvas.getContext('2d');
  const newChart = new Chart(ctx, chartJSConfig);

  chartInstances.set(canvas, newChart);
}

function extractTableData(table, options) {
  const headers = Array.from(table.querySelectorAll('thead th')).map(th => th.textContent.trim());
  const rows = Array.from(table.querySelectorAll('tbody tr')).map(tr => {
    const cells = Array.from(tr.querySelectorAll('td')).map(td => td.textContent.trim());
    const obj = {};
    headers.forEach((header, i) => {
      obj[header] = isNaN(cells[i]) ? cells[i] : parseFloat(cells[i]);
    });
    return obj;
  });

  const labels = rows.map(row => row[headers[0]]); // First column = labels
  const valueHeaders = headers.slice(1); // All other columns = datasets

  const datasets = valueHeaders.map((header, idx) => ({
    label: header,
    data: rows.map(row => row[header]),
    backgroundColor: (options.colors && options.colors[idx % options.colors.length]) || generateColor(idx, 0.5),
    borderColor: (options.colors && options.colors[idx % options.colors.length]) || generateColor(idx, 1),
    borderWidth: 2,
    fill: options.chartType === 'line' ? false : true,
  }));

  return { labels, datasets };
}

function generateColor(index, alpha = 1) {
  const colors = [
    [255, 99, 132],   // red
    [54, 162, 235],   // blue
    [255, 206, 86],   // yellow
    [75, 192, 192],   // teal
    [153, 102, 255],  // purple
    [255, 159, 64],   // orange
    [199, 199, 199],  // grey
    [83, 102, 255],   // indigo
  ];
  const color = colors[index % colors.length];
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})`;
}

function parseColors(colorString) {
  if (!colorString) return null;
  return colorString.split(',').map(color => color.trim());
}
